import * as React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import BusinessTripRatesConfiguration, {
  BusinessTripRatesConfigurationStateProps,
  BusinessTripRatesConfigurationOwnProps,
} from './businessTripRatesConfiguration'
import { showMessage } from '@/modules/shared/stores/messageStore'
import { BusinessTripRatesConfigurationModel } from 'app/models/configuration/businessTripRatesConfigurationModel'
import { BusinessTripRatesRestoreOption } from 'app/constants/configuration/businessTripRatesRestoreOption'
import {
  businessTripRatesConfigKey,
  fetchBusinessTripRatesConfiguration,
  restoreBusinessTripRatesConfigurationRequest,
  saveBusinessTripRatesConfigurationRequest,
} from '@/modules/settings/services/configurationQueries'
import {
  countriesKey,
  currenciesKey,
  fetchCountries,
  fetchCurrencies,
} from './businessTripDictionaries'

/**
 * Stawki delegacji bez reduxa.
 *
 * Ekran pobiera wszystko sam - na mount i przy zmianie firmy - więc zapytania tylko
 * obserwują cache (`enabled: false`), a pobieraniem zajmują się funkcje niżej,
 * jak dawniej thunki. Przywrócenie domyślnych odświeża konfigurację, bo reducer
 * też ją wtedy podmieniał; zapis - nie, bo nie podmieniał.
 */
const BusinessTripRatesContainer: React.FC<
  BusinessTripRatesConfigurationOwnProps
> = (props) => {
  const queryClient = useQueryClient()
  const companyId = props.companyId

  const { data: countries = [] } = useQuery({
    queryKey: countriesKey(companyId),
    queryFn: () => fetchCountries(companyId),
    enabled: false,
  })
  const { data: currencies = [] } = useQuery({
    queryKey: currenciesKey(companyId),
    queryFn: () => fetchCurrencies(companyId),
    enabled: false,
  })
  const { data: configuration = null } = useQuery({
    queryKey: businessTripRatesConfigKey(companyId),
    queryFn: () => fetchBusinessTripRatesConfiguration(companyId),
    enabled: false,
  })

  const getCountries = React.useCallback(
    (id: string) =>
      queryClient.fetchQuery({
        queryKey: countriesKey(id),
        queryFn: () => fetchCountries(id),
      }),
    [queryClient],
  )
  const getCurrencies = React.useCallback(
    (id: string) =>
      queryClient.fetchQuery({
        queryKey: currenciesKey(id),
        queryFn: () => fetchCurrencies(id),
      }),
    [queryClient],
  )
  const getBusinessTripRatesConfiguration = React.useCallback(
    (id: string) =>
      queryClient.fetchQuery({
        queryKey: businessTripRatesConfigKey(id),
        queryFn: () => fetchBusinessTripRatesConfiguration(id),
      }),
    [queryClient],
  )

  const saveBusinessTripRatesConfiguration = React.useCallback(
    (id: string, model: BusinessTripRatesConfigurationModel) =>
      saveBusinessTripRatesConfigurationRequest(id, model),
    [],
  )

  const restoreBusinessTripRatesConfiguration = React.useCallback(
    async (id: string, restoreOption: BusinessTripRatesRestoreOption) => {
      const restored = await restoreBusinessTripRatesConfigurationRequest(
        id,
        restoreOption,
      )
      queryClient.setQueryData(businessTripRatesConfigKey(id), restored)
      return restored
    },
    [queryClient],
  )

  return (
    <BusinessTripRatesConfiguration
      {...props}
      companyId={companyId}
      countries={countries}
      currencies={currencies}
      configuration={
        configuration as BusinessTripRatesConfigurationStateProps['configuration']
      }
      showMessage={showMessage}
      getCountries={getCountries}
      getCurrencies={getCurrencies}
      // Prop deklaruje listę, ale endpoint zwraca pojedynczą konfigurację - tak samo
      // było przy thunku, którego typ gubił się na `dispatch(...)`.
      getBusinessTripRatesConfiguration={
        getBusinessTripRatesConfiguration as any
      }
      saveBusinessTripRatesConfiguration={saveBusinessTripRatesConfiguration}
      restoreBusinessTripRatesConfiguration={
        restoreBusinessTripRatesConfiguration
      }
    />
  )
}

export default BusinessTripRatesContainer
